import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, CheckCircle2, Handshake } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Seo } from "@/components/Seo";
import { toast } from "sonner";

const EMPTY = { partner_name: "", partner_email: "", firm_name: "", contact_name: "", contact_email: "", contact_phone: "", reason: "", notes: "", consent: false };

export default function PartnerReferral() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please confirm the firm has agreed to be introduced."); return; }
    setLoading(true);
    try {
      await api.post("/public/partner-referral", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1F33] text-white font-body">
      <Seo title="Partner Referral — RetainProof" description="Refer a law firm to RetainProof. Make a qualified introduction and earn 10% of the firm's first paid engagement, capped at $500 per new client." path="/refer" />
      <header className="h-16 flex items-center justify-between px-6 sm:px-10 border-b border-white/10">
        <Link to="/" className="flex items-center gap-2.5 font-body font-bold text-2xl tracking-tight" data-testid="refer-brand-link">
          <img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-8 w-auto" />
          <span>Retain<span className="text-[#0E7C7B]">Proof</span><sup className="text-[0.5em] font-normal align-super">™</sup></span>
        </Link>
        <Link to="/" className="font-mono-data text-xs uppercase tracking-widest text-zinc-400 hover:text-white">← Home</Link>
      </header>

      <div className="max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12 items-start">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#D5A44A] mb-4 flex items-center gap-2"><Handshake className="h-4 w-4" /> Partner referral</p>
          <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">Refer a firm.<br /><span className="italic text-[#D5A44A]">We do the rest.</span></h1>
          <p className="mt-5 text-white/60 text-base max-w-xl leading-relaxed">
            Make a qualified introduction and RetainProof manages the consultation, proposal and engagement. You're compensated when the introduction becomes a paying client.
          </p>
          <ul className="mt-8 space-y-3 text-sm text-zinc-300">
            {["10% of the firm's first paid engagement", "Capped at $500 per new client", "Paid after the client signs and pays", "No exclusivity required"].map((t) => (
              <li key={t} className="flex gap-2.5"><CheckCircle2 className="h-4 w-4 text-[#0E7C7B] shrink-0 mt-0.5" />{t}</li>
            ))}
          </ul>
          <p className="mt-8 text-xs text-white/40 max-w-md leading-relaxed">
            Referral compensation is earned only after the referred client signs an agreement and RetainProof has received payment. All arrangements are subject to written terms.
          </p>
        </div>

        {done ? (
          <div className="border border-white/10 bg-white/5 p-10 text-center" data-testid="refer-success">
            <CheckCircle2 className="h-12 w-12 text-[#0E7C7B] mx-auto" />
            <h2 className="font-display text-2xl mt-5">Introduction received</h2>
            <p className="text-white/60 mt-3">Thank you. We'll reach out to the firm and keep you posted. We appreciate the referral.</p>
            <Link to="/"><Button className="mt-8 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none">Back to home</Button></Link>
          </div>
        ) : (
          <form onSubmit={submit} className="border border-white/10 bg-white/5 p-8 space-y-4" data-testid="refer-form">
            <h2 className="font-display text-2xl">Submit a referral</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Input required placeholder="Your name" value={form.partner_name} onChange={set("partner_name")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-partner-name" />
              <Input required type="email" placeholder="Your email" value={form.partner_email} onChange={set("partner_email")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-partner-email" />
            </div>
            <Input required placeholder="Referred law firm name" value={form.firm_name} onChange={set("firm_name")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-firm-name" />
            <div className="grid sm:grid-cols-2 gap-4">
              <Input placeholder="Firm contact name (optional)" value={form.contact_name} onChange={set("contact_name")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-contact-name" />
              <Input type="email" placeholder="Contact email (optional)" value={form.contact_email} onChange={set("contact_email")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-contact-email" />
            </div>
            <Input placeholder="Contact phone (optional)" value={form.contact_phone} onChange={set("contact_phone")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-contact-phone" />
            <Input placeholder="Reason for referral (optional)" value={form.reason} onChange={set("reason")} className="rounded-none bg-black/30 border-white/15" data-testid="refer-reason" />
            <Textarea placeholder="Any background or notes? (optional)" value={form.notes} onChange={set("notes")} className="rounded-none bg-black/30 border-white/15 min-h-[90px]" data-testid="refer-notes" />
            <label className="flex items-start gap-3 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.consent} onChange={(e) => setForm((f) => ({ ...f, consent: e.target.checked }))} className="mt-1 accent-[#D5A44A] h-4 w-4" data-testid="refer-consent" />
              I confirm the firm has agreed to be introduced to RetainProof.
            </label>
            <Button type="submit" disabled={loading} data-testid="refer-submit" className="w-full bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none h-12 text-base font-medium group">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Submit referral <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </Button>
            <p className="text-xs text-white/40">Prefer email? Introduce us at <a href="mailto:clientcare@retainproofnow.com" className="text-[#D5A44A] hover:underline">clientcare@retainproofnow.com</a>.</p>
          </form>
        )}
      </div>
    </div>
  );
}
